import dayjs from 'dayjs';

import type { DayOfWeek } from 'app/typings/core/common/enum/dayOfWeek';
import type {
  Calendar,
  CalendarPeriod,
  CalendarSlot,
  CalendarSlotSeries,
} from 'app/typings/core/extranet/inventory/calendar';
import { FREQUENCY_MODE } from 'app/typings/core/extranet/inventory/enums/FrequencyMode';
import type { FrequencyMode } from 'app/typings/core/extranet/inventory/enums/FrequencyMode';

import type { TimeslotEvent } from '../_components/CalendarEvent/CalendarEvent';

import { adjustEndForMidnight } from './events';
import type { IsDayClosed } from './events';
import { DAY_OF_WEEK_TO_ISO_WEEKDAY } from './weekdays';

const DATE_FORMAT = 'YYYY-MM-DD';
const WEEK_LENGTH = 7;

const combine = (date: string, time: string): Date =>
  dayjs(`${date}T${time}`).toDate();

const frequencyFromRepeatDays = (repeatDays: DayOfWeek[]): FrequencyMode => {
  if (repeatDays.length >= WEEK_LENGTH) {
    return FREQUENCY_MODE.DAILY;
  }

  return repeatDays.length === 1
    ? FREQUENCY_MODE.WEEKLY
    : FREQUENCY_MODE.CUSTOM;
};

const slotToEvent = (
  slot: CalendarSlot,
  isDayClosed?: IsDayClosed
): TimeslotEvent => {
  const slotsLeft = Math.max(0, slot.stock - slot.booked);
  const start = combine(slot.date, slot.startTime);
  const end = adjustEndForMidnight(start, combine(slot.date, slot.endTime));

  return {
    title: '',
    start,
    end,
    resource: {
      id: String(slot.id),
      slotsLeft,
      totalBooked: slot.booked,
      hasStock: slotsLeft > 0,
      isRecurrent: false,
      isClosed: isDayClosed?.(dayjs(slot.date)) ?? false,
    },
  };
};

const seriesToEvents = (
  series: CalendarSlotSeries,
  period: CalendarPeriod,
  isDayClosed?: IsDayClosed
): TimeslotEvent[] => {
  const slotsLeft = Math.max(0, series.stock - series.booked);
  const weekdays = series.repeatDays.map(
    (day) => DAY_OF_WEEK_TO_ISO_WEEKDAY[day]
  );
  const frequency = frequencyFromRepeatDays(series.repeatDays);

  const periodStart = dayjs(period.startDate);
  const periodEnd = dayjs(period.endDate);

  return series.dates
    .map((date) => dayjs(date))
    .reduce((events: TimeslotEvent[], day) => {
      if (!day.isBefore(periodStart, 'day') && !day.isAfter(periodEnd, 'day')) {
        const dateStr = day.format(DATE_FORMAT);
        const start = combine(dateStr, series.startTime);
        const end = adjustEndForMidnight(
          start,
          combine(dateStr, series.endTime)
        );

        events.push({
          title: '',
          start,
          end,
          resource: {
            id: `series-${series.id}-${dateStr}`,
            seriesId: String(series.id),
            slotsLeft,
            totalBooked: series.booked,
            hasStock: slotsLeft > 0,
            isRecurrent: true,
            isClosed: isDayClosed?.(day) ?? false,
            frequency,
            weekdays,
          },
        });
      }

      return events;
    }, []);
};

export const mapCalendarToEvents = (
  calendar: Calendar | null | undefined,
  period: CalendarPeriod,
  isDayClosed?: IsDayClosed
): TimeslotEvent[] => {
  if (!calendar) {
    return [];
  }

  return [
    ...calendar.slots.map((slot) => slotToEvent(slot, isDayClosed)),
    ...calendar.series.flatMap((series) =>
      seriesToEvents(series, period, isDayClosed)
    ),
  ];
};
